<template>
  <div class="header">
    <div class="header__inner">
      <div
        class="flex justify-center items-center text-iconColor max-w-[142px] lg:max-w-full"
        style="mix-blend-mode: color"
      >
        <div
          class="text-foregroundPrimary cursor-pointer"
          @click="handleLogoClick"
        >
          <SezusMainLogo />
        </div>
      </div>
      <div class="flex" v-if="isUserAuth && userdata">
        <Dropdown
          class="current-org-drops min-w-[140px] max-w-[140px] flex items-center justify-center rounded-[10px] w-full font-bold max-h-[34px] min-h-[34px] mr-2 mt-1 !px-0"
          v-model="selectedProfile"
          :options="filteredOrgs"
          optionLabel="name"
          @change="setCurrentProfile(selectedProfile)"
          placeholder="Select profile"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex align-items-center">
              <BaseAvatar
                :storage-id="slotProps.value?.avatarFile?.storageId"
                class="!max-w-[25px] !max-h-[25px] !min-w-[25px] mr-2"
              />
              <div
                class="!text-[var(--tw-text-opacity, 1)] !text-[0.7rem] flex items-center"
              >
                <span class="dropdown-menu__caption !text-[0.7rem]">{{
                  slotProps.value.username.slice(0, 7) + ".."
                }}</span>
              </div>
            </div>
            <span v-else>
              {{ slotProps.placeholder }}
            </span>
          </template>
          <template #option="slotProps">
            <div class="flex align-items-center">
              <BaseAvatar
                :storage-id="slotProps.option?.avatarFile?.storageId"
                class="!max-w-[25px] !max-h-[25px] mr-2"
              />
              <div class="!text-[var(--tw-text-opacity, 1)] text-[0.9rem]">
                <span class="dropdown-menu__caption">{{
                  slotProps.option.username
                }}</span>
              </div>
            </div>
          </template>
        </Dropdown>
        <router-link
          class="header__settings flex cursor-pointer items-center"
          :to="createUserRoute()"
        >
          <BaseAvatar
            :storage-id="userdata?.avatarFile?.storageId"
            :href="createUserRoute()"
            :isAdmin="isAdmin"
            :verified="userdata?.verified"
            :rating="userdata?.rating"
            :isCurrentUser="true"
          />
          <div class="ml-2">
            <ReputationBadge
              v-if="userdata?.rating"
              :score="userdata.rating"
              :showTierName="true"
              :isCurrentUser="true"
              :tierSet="tierSet"
              class="prominent-badge"
            />
          </div>
          <!-- <div
            class="flex justify-center items-center header-arrow w-4 text-iconColor"
          >
            <ArrowRightIcon />
          </div> -->
        </router-link>
        <div
          @click="toNotifications"
          class="flex justify-center items-center ml-[12px] cursor-pointer text-iconColor02 relative"
        >
          <img
            :src="getAlertIcon"
            alt="Alerts"
            class="w-[20px] h-[20px]"
            :class="{ 'bell-shake': isShaking }"
          />
          <span
            v-if="unreadNotificationsCount > 0"
            class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
          >
            {{
              unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount
            }}
          </span>
        </div>
        <ColorSchemeSwitcher />
      </div>
      <div class="flex" v-else>
        <div class="sm:flex">
          <div class="leftbar__items">
            <router-link
              to="/about"
              class="leftbar__overlay-item text-foregroundPrimary"
            >
              <ChannelIcon class="ml-10 w-[20px]" />
              About Us
            </router-link>
          </div>
          <BaseButton
            style="margin-left: 10px; margin-right: 10px"
            @click="logIn"
            >Sign in</BaseButton
          >
          <BaseButton class="max-w-[100px]" @click="signUp">Sign up</BaseButton>
          <SIWFButton style="margin-left: 10px" @click="siwf" />
        </div>
        <ColorSchemeSwitcher />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";

import { RouterLink, useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Dropdown from "primevue/dropdown";
import ColorSchemeSwitcher from "@/components/ColorSchemeSwitcher/index.vue";
import ArrowRightIcon from "@/components/Icons/ArrowRightIcon.vue";
import SezusMainLogo from "@/components/Icons/SezusMainLogo.vue";
import ReputationBadge from "@/components/ReputationBadge.vue";
import SIWFButton from "@/components/ui-kit/SIWFButton/index.vue";
import eventBus from "@/utils/eventBus";
import {
  abbreviateNumber,
  scoreToPercentileSync,
  formatPercentile,
} from "@/utils/utils.js";
import { useSchemeObserver } from "@/utils/colorSchemeUtils";
import { useTierSet } from "@/utils/reputationTiers.js";

const store = useStore();
const route = useRoute();
const router = useRouter();
const userdata = computed(() => store.getters.getUser);

// Tier set for reputation badges
const { tierSet } = useTierSet();
const isUserAuth = computed(() => store.getters.getAuthStatus);
const isShowModal = (value) => store.dispatch("showModal", value);
const getMyOrgs = (search = "") =>
  store.dispatch("fetchOnlyUserOrgs", { search });
const handleSetOrgProfile = (data) =>
  store.dispatch("fetchCurrentProfileOrg", data);
const isAdmin = computed(
  () => userdata?.value?.roles?.includes("admin") || false
);
const myOrgs = computed(() => store.getters.getOnlyUserOrgs);
const orgInfo = computed(() => store.getters.getOrgData);
const currentOrgProfile = computed(() => store.getters.getCurrentOrgProfile);
const filteredOrgs = ref([]);
const selectedProfile = ref(null);

const unreadNotificationsCount = computed(
  () => store.getters.getUnreadNotificationsCount
);
const isShaking = ref(false);

const hasUnreadNotification = computed(() => {
  const newNotificationsCount = store.getters.getUnreadNotificationsCount;
  return newNotificationsCount > 0;
});

// Trigger shake animation
const triggerShake = () => {
  isShaking.value = true;
  setTimeout(() => {
    isShaking.value = false;
  }, 500);
};

// Calculate the user's percentile based on their rating
const userPercentile = computed(() => {
  if (!userdata.value?.rating) return "-";
  const percentile = scoreToPercentileSync(userdata.value.rating);
  return formatPercentile(percentile, "short");
});

// Tooltip showing both percentile and raw score
const reputationTooltip = computed(() => {
  if (!userdata.value?.rating) return "Reputation: N/A";
  const percentile = scoreToPercentileSync(userdata.value.rating);
  const rank = formatPercentile(percentile, "rank");
  return `${rank} (Raw score: ${userdata.value.rating})`;
});

const createUserRoute = () => {
  return !userdata.value?.username
    ? `/profile/${userdata.value?.id}`
    : `/user/${userdata.value?.username}`;
};

watch(
  () => currentOrgProfile.value,
  () => {
    selectedProfile.value = currentOrgProfile.value;
  }
);

watch(
  () => route.name,
  (newId, oldId) => {
    // filteredOrgs.value = myOrgs.value.filter(org => {
    //   if (org.id === orgInfo.value?.id) {
    //     return org;
    //   }
    // });
  }
);

// Watch for notification count changes and trigger shake
watch(unreadNotificationsCount, (newCount, oldCount) => {
  // Only shake if count increased (new notification arrived)
  if (newCount > oldCount && oldCount !== undefined) {
    triggerShake();
  }
});

// Watch for user authentication to shake on login if notifications exist
watch(
  () => userdata.value,
  (newUser, oldUser) => {
    // Trigger shake when user logs in and has unread notifications
    if (newUser && !oldUser && unreadNotificationsCount.value > 0) {
      // Delay slightly to ensure UI is ready
      setTimeout(() => {
        triggerShake();
      }, 1000);
    }
  }
);

onMounted(async () => {
  if (userdata.value?.id) {
    await getMyOrgs();
    selectedProfile.value = currentOrgProfile.value;
    filteredOrgs.value = myOrgs.value.filter((org) => {
      let user = org.members.filter(
        (us) => us.userId === userdata.value?.id
      )[0];
      if (user?.role && user?.role !== "reader" && org.isPrivate === false) {
        return org;
      }
    });
    filteredOrgs.value.unshift(userdata.value);
  }
});

const setCurrentProfile = (data) => {
  handleSetOrgProfile(data);
};

const siwf = () => {
  router.push({
    redirect: (window.location.href = import.meta.env.VITE_SIWF_LOGIN_URL),
  });
};

const show = () => {
  isShowModal(true);
};
const toHome = () => {
  eventBus.emit("set-default-tab");
  return "/";
};

const toNotifications = () => {
  router.push("/notifications");
};

const toProfile = () => {
  if (userdata.value && userdata.value.id) {
    router.push(`/profile/${userdata.value.id}`);
  } else {
    console.error("User ID is missing");
  }
};
const logIn = () => {
  router.push("/sign-in");
  localStorage.removeItem("activeTab");
};
const signUp = () => {
  router.push("/onboarding/login-method");
};

const handleLogoClick = () => {
  console.log("Logo clicked");
  router.push(toHome());
};

const currentScheme = useSchemeObserver();

const getAlertIcon = computed(() => {
  const scheme = currentScheme.value;
  const hasMessages = hasUnreadNotification.value;
  const isActive = router.currentRoute.value.path.startsWith("/notifications");

  if (isActive) {
    return hasMessages
      ? `/icon/alert-unread-fill${scheme === "light" ? "-dark" : ""}.svg`
      : `/icon/Alertfill${scheme === "light" ? "-dark" : ""}.svg`;
  } else {
    return hasMessages
      ? `/icon/alert-unread${scheme === "light" ? "-dark" : ""}.svg`
      : `/icon/Alert${scheme === "light" ? "-dark" : ""}.svg`;
  }
});
</script>

<style lang="scss" scoped>
.header {
  position: fixed;
  display: flex;
  align-items: center;
  width: 100%;
  height: 80px;
  max-height: 80px;
  max-width: 1280px;
  padding: 16px 0;
  z-index: 1200;
  background: var(--background-primary);

  &__inner {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    isolation: isolate;
  }

  &__rating {
    font-family: $clash;
    color: var(--foreground-primary);
    position: relative;
    cursor: help;
  }

  &__settings {
    display: flex;
    align-items: center;
    position: relative;

    .prominent-badge {
      position: relative;
      z-index: 1;
    }

    &:hover .header-arrow {
      transform: translateX(2px);
    }
  }

  @include _1400 {
    padding: 16px 20px;
  }

  @include _1279 {
    height: 64px;
    padding: 16px 48px;
  }

  @include _1024 {
    padding: 16px 44px;

    img {
      max-width: 90px;
    }
  }

  @include _767 {
    padding: 16px 32px;
  }

  @include _575 {
    padding: 16px;
  }
}

.current-org-drops {
  @include _575 {
    position: absolute;
    top: 56px;
    right: 10px;
  }
}

.header-arrow {
  transition: 0.2s ease-in-out;
  width: 7px;
  margin-left: 4px;
}

@keyframes bellShake {
  0%,
  100% {
    transform: rotate(0deg);
  }
  10% {
    transform: rotate(-10deg);
  }
  20% {
    transform: rotate(10deg);
  }
  30% {
    transform: rotate(-10deg);
  }
  40% {
    transform: rotate(10deg);
  }
  50% {
    transform: rotate(-5deg);
  }
  60% {
    transform: rotate(5deg);
  }
  70% {
    transform: rotate(-5deg);
  }
  80% {
    transform: rotate(5deg);
  }
  90% {
    transform: rotate(0deg);
  }
}

.bell-shake {
  animation: bellShake 0.5s ease-in-out;
}
</style>
